import React, { useEffect, useState } from 'react';
import {
  getWhatsAppSlaSettings,
  updateWhatsAppSlaSettings,
  getWhatsAppIntegrations,
  createWhatsAppIntegration,
  updateWhatsAppIntegration,
  deleteWhatsAppIntegration,
  getSmartSuggestionsEnabled,
  setSmartSuggestionsEnabled,
  getAutoRemindersEnabled,
  setAutoRemindersEnabled,
  getAutoAssignEnabled,
  setAutoAssignEnabled,
  revealWhatsAppIntegrationToken
} from '../utils/api';
import { useToast } from './Toast';
const StatusBadge = ({ status, language }) => {
  const map = {
    connected:    { bg: '#dcfce7', color: '#15803d', dot: '#22c55e', label: language === 'ar' ? 'متصل'  : 'Connected'    },
    pending:      { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: language === 'ar' ? 'معلق'   : 'Pending'      },
    disconnected: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', label: language === 'ar' ? 'منفصل'  : 'Disconnected' },
  };
  const s = map[status] || map.disconnected;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: s.bg, color: s.color, borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: 700 }}>
      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
      {s.label}
    </span>
  );
};

const IdChip = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#475569' }}>
      <span style={{ fontWeight: 600, color: '#64748b', minWidth: '60px' }}>{label}</span>
      <span style={{ fontFamily: 'monospace', background: '#f1f5f9', borderRadius: '5px', padding: '1px 7px', color: '#334155', letterSpacing: '0.3px' }}>{value}</span>
      <button onClick={copy} title="Copy" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: copied ? '#22c55e' : '#94a3b8', fontSize: '11px', borderRadius: '4px' }}>
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  );
};

const calculateDaysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const TokenExpiryBadge = ({ tokenExpiration, language, userRole }) => {
  if (!tokenExpiration?.expiresAt) return null;

  const daysUntilExpiry = calculateDaysUntilExpiry(tokenExpiration.expiresAt);
  const isExpiringSoon = daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
  const isExpired = daysUntilExpiry < 0;
  const roleNorm = String(userRole || '').trim().toLowerCase();
  const canSeeWarnings = roleNorm === 'owner' || roleNorm === 'manager';

  // Only show badge if expired, or if expiring soon and user can see warnings
  if (!isExpired && !(isExpiringSoon && canSeeWarnings)) return null;

  let bgColor, textColor, icon, message;

  if (isExpired) {
    bgColor = '#fee2e2';
    textColor = '#991b1b';
    icon = '⚠';
    message = language === 'ar' ? 'منتهي' : 'Expired';
  } else {
    bgColor = '#fef3c7';
    textColor = '#92400e';
    icon = '⚠';
    message = language === 'ar' ? `${daysUntilExpiry}د` : `${daysUntilExpiry}d`;
  }

  return (
    <span style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '3px', 
      background: bgColor, 
      color: textColor, 
      borderRadius: '12px', 
      padding: '2px 7px', 
      fontSize: '10px', 
      fontWeight: 700,
      lineHeight: 1
    }}>
      <span style={{ fontSize: '8px' }}>{icon}</span>
      <span>{message}</span>
    </span>
  );
};

const TokenExpiryInfo = ({ tokenExpiration, language }) => {
  if (!tokenExpiration?.expiresAt) {
    return (
      <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
        {language === 'ar' ? 'معلومات انتهاء التوكن غير متوفرة' : 'Token expiry info unavailable'}
      </div>
    );
  }

  const daysUntilExpiry = calculateDaysUntilExpiry(tokenExpiration.expiresAt);
  const expiryDate = new Date(tokenExpiration.expiresAt);
  const isExpiringSoon = daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
  const isExpired = daysUntilExpiry < 0;

  let bgColor, textColor, icon, message;

  if (isExpired) {
    bgColor = '#fee2e2';
    textColor = '#991b1b';
    icon = '🚨';
    message = language === 'ar' ? 'انتهت صلاحية التوكن' : 'Token expired';
  } else if (isExpiringSoon) {
    bgColor = '#fef3c7';
    textColor = '#92400e';
    icon = '⚠️';
    message = language === 'ar' 
      ? `ينتهي خلال ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'يوم' : 'أيام'}`
      : `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`;
  } else {
    bgColor = '#f0f9ff';
    textColor = '#0369a1';
    icon = '✅';
    message = language === 'ar' 
      ? `ينتهي خلال ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'يوم' : 'أيام'}`
      : `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`;
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '5px', 
      background: bgColor, 
      color: textColor, 
      borderRadius: '6px', 
      padding: '3px 8px', 
      fontSize: '11px', 
      fontWeight: 600,
      marginTop: '4px'
    }}>
      <span>{icon}</span>
      <span>{message}</span>
      <span style={{ color: textColor, opacity: 0.7 }}>
        ({expiryDate.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')})
      </span>
    </div>
  );
};

const WhatsAppSettings = ({ language = 'ar', readOnly = false, userRole = null }) => {
  const toast = useToast();
  const roleNorm = String(userRole || '').trim().toLowerCase();
  const canRevealWhatsAppToken = roleNorm === 'owner' || roleNorm === 'manager';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationSaving, setIntegrationSaving] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [editingIntegrationId, setEditingIntegrationId] = useState(null);
  const [warningMinutes, setWarningMinutes] = useState(10);
  const [criticalMinutes, setCriticalMinutes] = useState(60);
  const [smartSuggestions, setSmartSuggestions] = useState(true);
  const [smartSuggestionsSaving, setSmartSuggestionsSaving] = useState(false);
  const [autoReminders, setAutoReminders] = useState(true);
  const [autoRemindersSaving, setAutoRemindersSaving] = useState(false);
  const [autoAssign, setAutoAssign] = useState(true);
  const [autoAssignSaving, setAutoAssignSaving] = useState(false);

  const [formData, setFormData] = useState({
    business_display_name: '',
    waba_id: '',
    phone_number_id: '',
    access_token: '',
    app_id: '',
    status: 'connected'
  });
  const [revealModal, setRevealModal] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);

  const resetForm = () => {
    setEditingIntegrationId(null);
    setFormData({
      business_display_name: '',
      waba_id: '',
      phone_number_id: '',
      access_token: '',
      app_id: '',
      status: 'connected'
    });
  };

  const loadSlaSettings = async () => {
    try {
      setLoading(true);
      const response = await getWhatsAppSlaSettings();
      setWarningMinutes(Number(response.data?.warning_minutes || 10));
      setCriticalMinutes(Number(response.data?.critical_minutes || 60));
    } catch (error) {
      console.error('Failed to load WhatsApp SLA settings:', error);
      toast.error(language === 'ar' ? 'تعذر تحميل إعدادات SLA' : 'Failed to load SLA settings');
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrations = async () => {
    try {
      setIntegrationLoading(true);
      const response = await getWhatsAppIntegrations(true); // Include expiration info
      setIntegrations(Array.isArray(response.data?.integrations) ? response.data.integrations : []);
    } catch (error) {
      console.error('Failed to load WhatsApp integrations:', error);
      toast.error(language === 'ar' ? 'تعذر تحميل التكاملات' : 'Failed to load integrations');
    } finally {
      setIntegrationLoading(false);
    }
  };

  const loadSmartSuggestions = async () => {
    try {
      const res = await getSmartSuggestionsEnabled();
      setSmartSuggestions(res.data?.enabled !== false);
    } catch { /* default true */ }
  };

  const handleToggleSmartSuggestions = async (val) => {
    try {
      setSmartSuggestionsSaving(true);
      await setSmartSuggestionsEnabled(val);
      setSmartSuggestions(val);
      toast.success(language === 'ar' ? 'تم الحفظ' : 'Saved');
    } catch {
      toast.error(language === 'ar' ? 'تعذر الحفظ' : 'Save failed');
    } finally {
      setSmartSuggestionsSaving(false);
    }
  };

  const loadAutoReminders = async () => {
    try {
      const res = await getAutoRemindersEnabled();
      setAutoReminders(res.data?.enabled !== false);
    } catch { /* default true */ }
  };

  const handleToggleAutoReminders = async (val) => {
    try {
      setAutoRemindersSaving(true);
      await setAutoRemindersEnabled(val);
      setAutoReminders(val);
      toast.success(language === 'ar' ? 'تم الحفظ' : 'Saved');
    } catch {
      toast.error(language === 'ar' ? 'تعذر الحفظ' : 'Save failed');
    } finally {
      setAutoRemindersSaving(false);
    }
  };

  const loadAutoAssign = async () => {
    try {
      const res = await getAutoAssignEnabled();
      setAutoAssign(res.data?.enabled !== false);
    } catch { /* default true */ }
  };

  const handleToggleAutoAssign = async (val) => {
    try {
      setAutoAssignSaving(true);
      await setAutoAssignEnabled(val);
      setAutoAssign(val);
      toast.success(language === 'ar' ? 'تم الحفظ' : 'Saved');
    } catch {
      toast.error(language === 'ar' ? 'تعذر الحفظ' : 'Save failed');
    } finally {
      setAutoAssignSaving(false);
    }
  };

  const loadSettings = async () => {
    await Promise.all([loadSlaSettings(), loadIntegrations(), loadSmartSuggestions(), loadAutoReminders(), loadAutoAssign()]);
  };

  useEffect(() => {
    if (readOnly) {
      loadIntegrations();
    } else {
    loadSettings();
    }
  }, [readOnly]);

  const handleSave = async () => {
    const warning = Number(warningMinutes);
    const critical = Number(criticalMinutes);
    if (!Number.isFinite(warning) || warning < 1) {
      toast.error(language === 'ar' ? 'وقت التحذير يجب أن يكون 1 دقيقة أو أكثر' : 'Warning must be at least 1 minute');
      return;
    }
    if (!Number.isFinite(critical) || critical <= warning) {
      toast.error(language === 'ar' ? 'الوقت الحرج يجب أن يكون أكبر من التحذير' : 'Critical must be greater than warning');
      return;
    }

    try {
      setSaving(true);
      const response = await updateWhatsAppSlaSettings({
        warning_minutes: warning,
        critical_minutes: critical
      });
      setWarningMinutes(Number(response.data?.warning_minutes || warning));
      setCriticalMinutes(Number(response.data?.critical_minutes || critical));
      toast.success(language === 'ar' ? 'تم حفظ إعدادات SLA' : 'SLA settings saved');
    } catch (error) {
      console.error('Failed to save WhatsApp SLA settings:', error);
      toast.error(error?.response?.data?.error || (language === 'ar' ? 'تعذر حفظ إعدادات SLA' : 'Failed to save SLA settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (integration) => {
    setEditingIntegrationId(integration.id);
    setFormData({
      business_display_name: integration.business_display_name || '',
      waba_id: integration.waba_id || '',
      phone_number_id: integration.phone_number_id || '',
      access_token: '',
      app_id: integration.app_id || '',
      status: integration.status || 'connected'
    });
  };

  const handleSaveIntegration = async () => {
    const payload = {
      business_display_name: String(formData.business_display_name || '').trim(),
      waba_id: String(formData.waba_id || '').trim(),
      phone_number_id: String(formData.phone_number_id || '').trim(),
    };

    if (!payload.phone_number_id) {
      toast.error(language === 'ar' ? 'phone_number_id مطلوب' : 'phone_number_id is required');
      return;
    }

    if (!editingIntegrationId && !String(formData.access_token || '').trim()) {
      toast.error(language === 'ar' ? 'access_token مطلوب' : 'access_token is required');
      return;
    }

    if (String(formData.access_token || '').trim()) {
      payload.access_token = String(formData.access_token).trim();
    }
    if (String(formData.app_id || '').trim()) {
      payload.app_id = String(formData.app_id).trim();
    }

    try {
      setIntegrationSaving(true);
      if (editingIntegrationId) {
        await updateWhatsAppIntegration(editingIntegrationId, payload);
        toast.success(language === 'ar' ? 'تم تحديث التكامل' : 'Integration updated');
      } else {
        await createWhatsAppIntegration(payload);
        toast.success(language === 'ar' ? 'تم إنشاء التكامل' : 'Integration created');
      }
      resetForm();
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to save integration:', error);
      toast.error(error?.response?.data?.error || (language === 'ar' ? 'تعذر حفظ التكامل' : 'Failed to save integration'));
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleToggleIntegrationStatus = async (integration) => {
    const isDisabling = integration.status === 'connected' || integration.status === 'pending';
    const confirmed = window.confirm(
      isDisabling
        ? (language === 'ar'
            ? 'سيتم تعطيل هذا التكامل. سيستمر Meta في إرسال الرسائل إلى الخادم لكن لن تظهر في صندوق الوارد. هل تريد المتابعة؟'
            : 'This will disable the integration. Meta will still deliver webhooks to your server but conversations will be hidden from the inbox. Continue?')
        : (language === 'ar'
            ? 'هل تريد إعادة تفعيل هذا التكامل؟'
            : 'Re-enable this integration?')
    );
    if (!confirmed) return;
    try {
      setIntegrationSaving(true);
      await updateWhatsAppIntegration(integration.id, {
        status: isDisabling ? 'disconnected' : 'connected',
      });
      toast.success(
        isDisabling
          ? (language === 'ar' ? 'تم تعطيل التكامل' : 'Integration disabled')
          : (language === 'ar' ? 'تم تفعيل التكامل' : 'Integration re-enabled')
      );
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to toggle integration status:', error);
      toast.error(error?.response?.data?.error || (language === 'ar' ? 'تعذّر تغيير الحالة' : 'Failed to update status'));
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    const confirmed = window.confirm(
      language === 'ar'
        ? 'هل أنت متأكد من حذف هذا التكامل؟'
        : 'Are you sure you want to delete this integration?'
    );
    if (!confirmed) return;

    try {
      setIntegrationSaving(true);
      await deleteWhatsAppIntegration(integrationId);
      toast.success(language === 'ar' ? 'تم حذف التكامل' : 'Integration deleted');
      if (editingIntegrationId === integrationId) resetForm();
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to delete integration:', error);
      toast.error(error?.response?.data?.error || (language === 'ar' ? 'تعذر حذف التكامل' : 'Failed to delete integration'));
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleRevealAccessToken = async (integration) => {
    const warn = language === 'ar'
      ? 'سيتم عرض التوكن الآن. لا تشاركه ولا تلتقط شاشة في مكان غير آمن. يُسجّل النظام هذا الإجراء. المتابعة؟'
      : 'The access token will be shown now. Do not share it or screenshot it in an unsafe place. This action is logged. Continue?';
    if (!window.confirm(warn)) return;
    try {
      setRevealLoading(true);
      const res = await revealWhatsAppIntegrationToken(integration.id);
      const token = String(res.data?.access_token || '').trim();
      if (!token) {
        toast.error(language === 'ar' ? 'لم يُعثر على توكن' : 'No token returned');
        return;
      }
      setRevealModal({
        token,
        label: integration.business_display_name || integration.phone_number_id || String(integration.id),
      });
    } catch (error) {
      console.error('reveal token:', error);
      toast.error(error?.response?.data?.error || (language === 'ar' ? 'تعذر عرض التوكن' : 'Could not reveal token'));
    } finally {
      setRevealLoading(false);
    }
  };

  return (
    <div className="wa-animate-in" style={{ padding: '24px' }} dir={language === 'ar' ? 'rtl' : 'ltr'}>

      {readOnly && (
        <div
          style={{
            maxWidth: '860px',
            marginBottom: '16px',
            padding: '14px 18px',
            borderRadius: '12px',
            border: '1px solid #bae6fd',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            color: '#0c4a6e',
            fontSize: '14px',
            lineHeight: 1.55,
          }}
        >
          <strong>{language === 'ar' ? 'عرض فقط' : 'View only'}</strong>
          {' — '}
          {language === 'ar'
            ? 'تعرض هذه الصفحة أرقام واتساب النشاط المتصلة بالمنظمة. لا يمكن تعديل التكامل أو الرموز من هنا — اطلب من المالك أو المدير عند الحاجة.'
            : 'This page shows your organization’s connected WhatsApp Business lines. Tokens and integration changes are managed by an owner or manager.'}
        </div>
      )}

      {/* Smart Suggestions + SLA — owner/manager only */}
      {!readOnly && (
        <>
      <div style={{ maxWidth: '860px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚡ {language === 'ar' ? 'المقترحات الذكية' : 'Smart Suggestions'}
            </h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '13px', lineHeight: '1.6' }}>
              {language === 'ar'
                ? 'عند رد العميل على رسالة تفاعلية مرتبطة بإجراء، يظهر تذكير للموظف في المحادثة لجدولة نشاط أو متابعة. يمكن تهيئة الإجراءات من قسم الرسائل التفاعلية.'
                : 'When a client replies to an interactive message that has a rule, a suggestion card appears in the conversation to schedule an activity. Rules are configured per button in the Interactive Messages section.'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <span style={{ fontSize: '13px', color: smartSuggestions ? '#15803d' : '#94a3b8', fontWeight: 600 }}>
              {smartSuggestions ? (language === 'ar' ? 'مفعّل' : 'On') : (language === 'ar' ? 'معطّل' : 'Off')}
            </span>
            <button
              disabled={smartSuggestionsSaving}
              onClick={() => handleToggleSmartSuggestions(!smartSuggestions)}
              style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: smartSuggestions ? '#22c55e' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: smartSuggestions ? '23px' : '3px',
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
              }} />
            </button>
          </div>
        </div>
      </div>

      {/* Auto Follow-up Reminders toggle */}
      <div style={{ maxWidth: '860px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔔 {language === 'ar' ? 'تذكيرات المتابعة التلقائية' : 'Auto Follow-up Reminders'}
            </h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '13px', lineHeight: '1.6' }}>
              {language === 'ar'
                ? 'ينشئ النظام تذكيراً تلقائياً عند عدم التواصل مع العميل خلال المدة المحددة حسب أهميته. يعتمد على آخر رسالة صادرة عبر واتساب.'
                : 'Automatically creates a follow-up reminder when an agent has not sent a WhatsApp message to a client within the configured window based on their importance level.'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <span style={{ fontSize: '13px', color: autoReminders ? '#15803d' : '#94a3b8', fontWeight: 600 }}>
              {autoReminders ? (language === 'ar' ? 'مفعّل' : 'On') : (language === 'ar' ? 'معطّل' : 'Off')}
            </span>
            <button
              disabled={autoRemindersSaving}
              onClick={() => handleToggleAutoReminders(!autoReminders)}
              style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: autoReminders ? '#22c55e' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: autoReminders ? '23px' : '3px',
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
              }} />
            </button>
          </div>
        </div>
      </div>

      {/* Inbound Auto-Assign toggle */}
      <div style={{ maxWidth: '860px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚡ {language === 'ar' ? 'التعيين التلقائي للمحادثات الواردة' : 'Inbound Auto-Assign'}
            </h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '13px', lineHeight: '1.6' }}>
              {language === 'ar'
                ? 'عند وصول رسالة واردة على محادثة غير معيّنة، يقوم النظام تلقائياً بتعيينها للموظف الأقل انشغالاً (حسب عدد المحادثات النشطة). لا يؤثر على المحادثات المعيّنة مسبقاً.'
                : 'When an inbound message arrives on an unassigned conversation, the system automatically assigns it to the least-loaded agent. Already-assigned conversations are never overridden.'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <span style={{ fontSize: '13px', color: autoAssign ? '#15803d' : '#94a3b8', fontWeight: 600 }}>
              {autoAssign ? (language === 'ar' ? 'مفعّل' : 'On') : (language === 'ar' ? 'معطّل' : 'Off')}
            </span>
            <button
              disabled={autoAssignSaving}
              onClick={() => handleToggleAutoAssign(!autoAssign)}
              style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: autoAssign ? '#22c55e' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: autoAssign ? '23px' : '3px',
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
              }} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '860px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
        <h2 style={{ margin: '0 0 8px 0', color: '#0f172a', fontSize: '20px' }}>
          {language === 'ar' ? 'إعدادات مستوى الخدمة' : 'SLA Settings'}
        </h2>
        <p style={{ margin: '0 0 18px 0', color: '#64748b', fontSize: '13px' }}>
          {language === 'ar'
            ? 'يمكنك تحديد دقائق التحذير والتصعيد الحرج حسب سياسة العمل لديك.'
            : 'Set warning and critical escalation minutes based on your business policy.'}
        </p>

        {loading ? (
          <div style={{ color: '#64748b', fontSize: '13px' }}>
            {language === 'ar' ? 'جاري تحميل الإعدادات...' : 'Loading settings...'}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                  {language === 'ar' ? 'تحذير SLA (بالدقائق)' : 'SLA Warning (minutes)'}
                </label>
                <input
                  type="number"
                  min={1}
                  className="wa-input"
                  value={warningMinutes}
                  onChange={(e) => setWarningMinutes(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                  {language === 'ar' ? 'تصعيد حرج SLA (بالدقائق)' : 'SLA Critical (minutes)'}
                </label>
                <input
                  type="number"
                  min={2}
                  className="wa-input"
                  value={criticalMinutes}
                  onChange={(e) => setCriticalMinutes(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '16px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '10px 12px' }}>
              {language === 'ar'
                ? 'ملاحظة: نافذة واتساب 24 ساعة لإرسال الرسائل خارج القالب تبقى كما هي ولم تتغير بهذه الإعدادات.'
                : 'Note: WhatsApp 24-hour messaging window remains unchanged and is not affected by these settings.'}
            </div>

            <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
              <button
                type="button"
                className="wa-btn wa-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                  : (language === 'ar' ? 'حفظ الإعدادات' : 'Save settings')}
              </button>
              <button
                type="button"
                className="wa-btn wa-btn-secondary"
                onClick={loadSlaSettings}
                disabled={saving}
              >
                {language === 'ar' ? 'إعادة تحميل' : 'Reload'}
              </button>
            </div>
          </>
        )}
      </div>
        </>
      )}

      <div style={{ maxWidth: '860px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.857L.057 23.215a.75.75 0 0 0 .928.928l5.357-1.476A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.694-.497-5.241-1.367l-.376-.214-3.9 1.075 1.075-3.9-.214-.376A9.944 9.944 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            </div>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '18px', fontWeight: 700 }}>
              {language === 'ar' ? 'تكامل واتساب للأعمال' : 'WhatsApp Business Integrations'}
        </h2>
          </div>
          <button
            type="button"
            className="wa-btn wa-btn-secondary"
            onClick={loadIntegrations}
            disabled={integrationLoading || integrationSaving}
            style={{ fontSize: '12px' }}
          >
            {integrationLoading ? '...' : (language === 'ar' ? '↻ تحديث' : '↻ Refresh')}
          </button>
        </div>
        <p style={{ margin: '0 0 20px 0', color: '#475569', fontSize: '14px', lineHeight: 1.5 }}>
          {readOnly
            ? (language === 'ar'
              ? 'الأرقام المتصلة بالمنظمة — تُستخدم تلقائياً في صندوق الوارد.'
              : 'Lines connected to your organization — used automatically in the inbox.')
            : (language === 'ar'
              ? 'أضف بيانات اعتماد رقم واتساب لكل نشاط تجاري للتوجيه التلقائي للمحادثات.'
              : 'Add WhatsApp number credentials for each business for automatic conversation routing.')}
        </p>

        {/* Integrations list */}
        <div style={{ marginBottom: '20px' }}>
          {integrationLoading ? (
            <div style={{ color: '#64748b', fontSize: '13px', padding: '20px', textAlign: 'center' }}>
              {language === 'ar' ? 'جاري تحميل التكاملات...' : 'Loading integrations...'}
            </div>
          ) : integrations.length === 0 ? (
            <div style={{ color: '#64748b', fontSize: '14px', padding: '24px', textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: '12px' }}>
              {readOnly
                ? (language === 'ar' ? 'لا توجد أرقام متصلة بعد. يضيفها المالك أو المدير من هنا.' : 'No connected lines yet. An owner or manager adds them here.')
                : (language === 'ar' ? 'لا توجد تكاملات بعد. أضف أول رقم واتساب أدناه.' : 'No integrations yet. Add your first WhatsApp number below.')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {integrations.map((integration) => {
                const isEditing = editingIntegrationId === integration.id;
                const isDisconnected = integration.status === 'disconnected';
                return (
                  <div
                    key={integration.id}
                    style={{
                      border: `1.5px solid ${isDisconnected ? '#fecaca' : isEditing ? '#6366f1' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      padding: '16px 18px',
                      background: isDisconnected ? '#fff8f8' : isEditing ? '#f8f8ff' : '#fafafa',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                      {/* Left: info */}
                      <div style={{ flex: 1, minWidth: '220px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
                            {integration.business_display_name || (language === 'ar' ? 'بدون اسم' : 'Unnamed')}
                          </span>
                          <StatusBadge status={integration.status} language={language} />
                          <TokenExpiryBadge 
                            tokenExpiration={integration.token_expiration} 
                            language={language} 
                            userRole={userRole} 
                          />
                          {integration.meta_last_checked_at && (
                            <span style={{ fontSize: '11px', color: '#475569', background: '#f1f5f9', borderRadius: '6px', padding: '2px 8px', fontWeight: 600 }}>
                              {language === 'ar' ? 'تحقق: ' : 'Checked: '}
                              {new Date(integration.meta_last_checked_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                          )}
                          {isDisconnected && (
                            <span style={{ fontSize: '11px', color: '#dc2626', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', padding: '2px 8px', fontWeight: 600 }}>
                              {language === 'ar' ? '⚠ يلزم تحديث التوكن' : '⚠ Token needs renewal'}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <IdChip label={language === 'ar' ? 'رقم الهاتف' : 'Phone ID'} value={integration.phone_number_id} />
                          {integration.waba_id && <IdChip label="WABA ID" value={integration.waba_id} />}
                          {integration.app_id && <IdChip label="App ID" value={integration.app_id} />}
                          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', fontStyle: 'italic' }}>
                            {language === 'ar' ? 'التوكن: ' : 'Token: '}
                            <span style={{ fontFamily: 'monospace' }}>••••••••••••{String(integration.phone_number_id || '').slice(-4)}</span>
                            {' · '}
                            {language === 'ar' ? 'آخر تحديث: ' : 'Updated: '}
                            {integration.updated_at ? new Date(integration.updated_at).toLocaleDateString() : '—'}
                          </div>
                          {integration.token_expiration && (
                            <TokenExpiryInfo tokenExpiration={integration.token_expiration} language={language} />
                          )}
                        </div>
                      </div>
                      {/* Right: actions */}
                      {!readOnly && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                        {canRevealWhatsAppToken && (
                          <button
                            type="button"
                            className="wa-btn wa-btn-secondary"
                            onClick={() => handleRevealAccessToken(integration)}
                            disabled={integrationSaving || revealLoading}
                            style={{ fontSize: '12px', borderColor: '#c4b5fd', color: '#5b21b6' }}
                          >
                            {revealLoading ? '…' : (language === 'ar' ? '🔑 عرض التوكن' : '🔑 Reveal token')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="wa-btn wa-btn-secondary"
                          onClick={() => isEditing ? resetForm() : handleEdit(integration)}
                          disabled={integrationSaving}
                          style={{ fontSize: '13px' }}
                        >
                          {isEditing ? (language === 'ar' ? '✕ إلغاء' : '✕ Cancel') : (language === 'ar' ? '✎ تعديل' : '✎ Edit')}
                        </button>
                        <button
                          type="button"
                          className="wa-btn wa-btn-secondary"
                          onClick={() => handleToggleIntegrationStatus(integration)}
                          disabled={integrationSaving}
                          style={{
                            fontSize: '13px',
                            ...(isDisconnected
                              ? { borderColor: '#6ee7b7', color: '#065f46', background: '#f0fdf4' }
                              : { borderColor: '#fcd34d', color: '#92400e', background: '#fffbeb' })
                          }}
                          title={isDisconnected
                            ? (language === 'ar' ? 'إعادة تفعيل التكامل' : 'Re-enable integration')
                            : (language === 'ar' ? 'تعطيل التكامل مؤقتاً — Meta سيستمر بالإرسال لكن لن تُعرض الرسائل في الصندوق' : 'Disable integration — Meta keeps sending webhooks but conversations are hidden from the inbox')}
                        >
                          {isDisconnected
                            ? (language === 'ar' ? 'تفعيل' : 'Enable')
                            : (language === 'ar' ? 'تعطيل' : 'Disable')}
                        </button>
                        <button
                          type="button"
                          className="wa-btn"
                          style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', fontSize: '13px' }}
                          onClick={() => handleDeleteIntegration(integration.id)}
                          disabled={integrationSaving}
                        >
                          {language === 'ar' ? 'حذف' : 'Delete'}
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add / Edit form — hidden in read-only (employee view) */}
        {!readOnly && (
        <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: '#334155', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
              {editingIntegrationId ? '✎' : '+'}
            </span>
            {editingIntegrationId
              ? (language === 'ar' ? 'تعديل التكامل' : 'Edit Integration')
              : (language === 'ar' ? 'إضافة تكامل جديد' : 'Add New Integration')}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {language === 'ar' ? 'اسم النشاط' : 'Business Name'}
              </label>
          <input
            className="wa-input"
                placeholder={language === 'ar' ? 'مثال: شركة العقارات' : 'e.g. Real Estate Company'}
            value={formData.business_display_name}
            onChange={(e) => handleFormChange('business_display_name', e.target.value)}
                style={{ width: '100%' }}
          />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                WABA ID
              </label>
          <input
            className="wa-input"
                placeholder={language === 'ar' ? 'مثال: 1234567890123456' : 'e.g. 1234567890123456'}
            value={formData.waba_id}
            onChange={(e) => handleFormChange('waba_id', e.target.value)}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
          />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Phone Number ID <span style={{ color: '#ef4444' }}>*</span>
              </label>
          <input
            className="wa-input"
                placeholder={language === 'ar' ? 'مثال: 9876543210987654' : 'e.g. 9876543210987654'}
            value={formData.phone_number_id}
            onChange={(e) => handleFormChange('phone_number_id', e.target.value)}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                App ID
              </label>
              <input
                className="wa-input"
                placeholder={language === 'ar' ? 'مثال: 1112223334445556' : 'e.g. 1112223334445556'}
                value={formData.app_id}
                onChange={(e) => handleFormChange('app_id', e.target.value)}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
              />
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px' }}>
                {language === 'ar' ? 'Meta Developer Console → App ID' : 'Meta Developer Console → App ID'}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Access Token {!editingIntegrationId && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
          <input
            className="wa-input"
                type="password"
            placeholder={editingIntegrationId
                  ? (language === 'ar' ? 'اتركه فارغًا للإبقاء على التوكن الحالي' : 'Leave blank to keep current token')
                  : (language === 'ar' ? 'الصق التوكن من Meta Business Suite' : 'Paste token from Meta Business Suite')}
            value={formData.access_token}
            onChange={(e) => handleFormChange('access_token', e.target.value)}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
              />
              {editingIntegrationId && (
                <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '3px', fontWeight: 600 }}>
                  {language === 'ar' ? '⚡ لتجديد الاتصال: أدخل توكن جديد من Meta Business Suite ← System Users ← Generate Token' : '⚡ To reconnect: enter a new token from Meta Business Suite → System Users → Generate Token'}
                </div>
              )}
              {editingIntegrationId && canRevealWhatsAppToken && (
                <div style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    className="wa-btn wa-btn-secondary"
                    onClick={() => {
                      const row = integrations.find((i) => String(i.id) === String(editingIntegrationId));
                      if (row) handleRevealAccessToken(row);
                    }}
                    disabled={integrationSaving || revealLoading}
                    style={{ fontSize: '12px', borderColor: '#c4b5fd', color: '#5b21b6' }}
                  >
                    {language === 'ar' ? '🔑 عرض التوكن المحفوظ (مالك أو مدير)' : '🔑 Reveal saved token (owner or manager)'}
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px', lineHeight: '1.5' }}>
                {language === 'ar'
                  ? '✓ الحالة تُحدَّث تلقائياً عند التحميل بالتحقق من Meta'
                  : '✓ Status is auto-verified from Meta on every load'}
              </div>
            </div>
        </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="wa-btn wa-btn-primary"
            onClick={handleSaveIntegration}
            disabled={integrationSaving}
          >
            {integrationSaving
              ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
              : (editingIntegrationId
                  ? (language === 'ar' ? '✓ تحديث التكامل' : '✓ Update Integration')
                  : (language === 'ar' ? '+ إضافة تكامل' : '+ Add Integration'))}
          </button>
          {editingIntegrationId && (
            <button
              type="button"
              className="wa-btn wa-btn-secondary"
              onClick={resetForm}
              disabled={integrationSaving}
            >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
          )}
          </div>
        </div>
        )}
            </div>

      {revealModal && (
        <div
          role="dialog"
          aria-modal="true"
                style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            zIndex: 10000,
                  display: 'flex',
                  alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setRevealModal(null)}
          dir={language === 'ar' ? 'rtl' : 'ltr'}
        >
          <div
            style={{
              background: '#fff',
              maxWidth: '540px',
              width: '100%',
              borderRadius: '14px',
              padding: '22px',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              border: '1px solid #e2e8f0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: '17px', color: '#0f172a' }}>
              {language === 'ar' ? 'رمز الوصول (Access Token)' : 'Access token'}
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#64748b', lineHeight: 1.55 }}>
              {language === 'ar'
                ? `التكامل: ${revealModal.label}. انسخ التوكن الآن — لن يُعرض مرة أخرى في هذه النافذة بعد الإغلاق.`
                : `Integration: ${revealModal.label}. Copy the token now — closing this dialog clears it from the screen.`}
            </p>
            <textarea
              readOnly
              value={revealModal.token}
              rows={4}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: '12px',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                marginBottom: '14px',
                boxSizing: 'border-box',
                wordBreak: 'break-all',
              }}
            />
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="wa-btn wa-btn-primary"
                onClick={() => {
                  navigator.clipboard?.writeText(revealModal.token).then(() => {
                    toast.success(language === 'ar' ? 'تم النسخ' : 'Copied');
                  }).catch(() => toast.error(language === 'ar' ? 'تعذر النسخ' : 'Copy failed'));
                }}
              >
                {language === 'ar' ? 'نسخ' : 'Copy'}
                  </button>
              <button type="button" className="wa-btn wa-btn-secondary" onClick={() => setRevealModal(null)}>
                {language === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                </div>
              </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppSettings;
